import { motion } from "framer-motion";
import { Phone, ClipboardList, PencilRuler, Hammer } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import Waves from "@/components/Waves";
import Seo from "@/components/Seo";
import { Bubbles } from "@/components/BackgroundFX";

const steps = [
  { icon: ClipboardList, title: "Send the details", desc: "Fill in the form — a photo helps us quote faster." },
  { icon: PencilRuler, title: "We scope it out", desc: "Paul reviews your job and comes back with a fair, straight price." },
  { icon: Hammer, title: "We build it", desc: "Cut, welded and finished in-house in Albany. No outsourcing." },
];

export default function QuotePage() {
  return (
    <div className="bg-[#003366]" data-testid="quote-page">
      <Seo
        title="Request an Aluminium Fabrication Quote | SAMI FAB Albany WA"
        description="Request a free quote from SAMI FAB for boat modifications, kit boat builds, boat maintenance, aluminium toolboxes, canopies, trailers and caravan accessories in Albany WA."
        keywords="aluminium fabrication quote Albany, boat modification quote, kit boat build quote, marine fabrication quote, custom toolbox quote, canopy quote, trailer fabrication quote"
        path="/quote"
      />
      <section className="relative overflow-hidden pt-[76px]">
        <Bubbles />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 text-white">
            <div className="overflow-hidden">
              <motion.p
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-blue-300"
              >
                Request a quote — Free, no obligation
              </motion.p>
            </div>
            <div className="overflow-hidden mt-4">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
                className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]"
              >
                Tell us what<br />you’re building.
              </motion.h1>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="mt-10 space-y-6 max-w-md"
              data-testid="quote-steps"
            >
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-4 items-start">
                  <div className="w-11 h-11 shrink-0 border border-white/25 flex items-center justify-center text-blue-300">
                    <s.icon size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-display font-bold uppercase tracking-tight text-base">
                      <span className="text-blue-300 mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {s.title}
                    </p>
                    <p className="text-sm text-blue-100/80 mt-1">{s.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.62 }}
              className="mt-10 border border-white/20 p-6 max-w-md"
            >
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">Rather talk it through?</p>
              <a
                href="tel:0415464900"
                data-testid="quote-call-link"
                className="mt-3 flex items-center gap-3 font-display font-black text-3xl hover:text-blue-300 transition-colors duration-200"
              >
                <Phone size={24} /> 0415 464 900
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="lg:col-span-7"
          >
            <QuoteForm />
          </motion.div>
        </div>
      </section>
      <Waves front="#1a1d20" back="rgba(0,123,255,0.35)" />
    </div>
  );
}
